import { useEffect, useLayoutEffect, useState } from 'react';
import type { RefObject } from 'react';

/** `useLayoutEffect` in the browser, so a measured width lands before the first paint. */
const useBrowserLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

/**
 * Border-box width of `ref`'s element, kept current with a `ResizeObserver` (HS-961). `0` means
 * "not measured": while `enabled` is false, before the first layout, on the server, and wherever
 * `ResizeObserver` does not exist (jsdom) — callers treat `0` as "fall back to the viewport".
 *
 * Measured in a layout effect, so a component that picks its markup from the width swaps it before
 * the browser paints the first guess. The observer also fires for height changes; those re-set the
 * same width and React skips the render.
 */
export function useContainerWidth(ref: RefObject<HTMLElement | null>, enabled: boolean): number {
  const [width, setWidth] = useState(0);

  useBrowserLayoutEffect(() => {
    const element = ref.current;
    if (!enabled || element == null || typeof ResizeObserver === 'undefined') {
      setWidth(0);
      return undefined;
    }
    const measure = () => {
      setWidth(element.offsetWidth);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, [ref, enabled]);

  return width;
}
